import type { TreeNode } from './Tree';

const helpers = {
  getNode<T extends TreeNode = TreeNode>(nodes: T[], id: T['id']): T | undefined {
    let found;
    nodes.some(node => {
      if (node.id === id) {
        found = node;
        return true;
      }

      if (node.nodes) {
        found = this.getNode(node.nodes, id);
        return !!found;
      }

      return false;
    });

    return found;
  },

  getParentNode<T extends TreeNode = TreeNode>(nodes: T[], id: T['id']): T | undefined {
    let found: T | undefined;
    nodes.some(node => {
      const childNodes = node.nodes || [];
      childNodes.some(childNode => {
        if (childNode.id === id) {
          found = node;
          return true;
        }

        if (childNode.nodes) {
          found = this.getParentNode(childNodes, id) as T;
          return !!found;
        }

        return false;
      });

      return !!found;
    });

    return found;
  },

  mapTree<T1 extends TreeNode = TreeNode, T2 extends TreeNode = T1>(
    nodes: T1[],
    fn: (node: T1, index: number, nodes: T1[]) => T2
  ): T2[] {
    return nodes.map((node, index, array) => {
      let newNode = node;

      if (node.nodes) {
        newNode = {
          ...newNode,
          nodes: this.mapTree(node.nodes as T1[], fn)
        };
      }

      return fn(newNode, index, array);
    });
  },

  forEachNode<T extends TreeNode = TreeNode>(
    nodes: T[],
    fn: (node: T, index: number, nodes: T[]) => void
  ): void {
    nodes.forEach((node, index, array) => {
      if (node.nodes) {
        this.forEachNode(node.nodes as T[], fn);
      }

      fn(node, index, array);
    });
  },

  mapNode<T1 extends TreeNode = TreeNode, T2 extends TreeNode = T1>(
    nodes: T1[],
    id: T1['id'],
    fn: (node: T1, index: number, array: T1[]) => T2
  ): (T1 | T2)[] {
    return nodes.map((node, index, array) => {
      let newNode: T1 | T2 = node;

      if (node.nodes) {
        newNode = {
          ...newNode,
          nodes: this.mapNode(node.nodes as T1[], id, fn)
        };
      }

      if (node.id === id) {
        newNode = fn(newNode, index, array);
      }

      return newNode;
    });
  },

  flatten<T extends TreeNode = TreeNode>(nodes: T[], ancestors?: T[]): (T & { ancestors?: T[] })[] {
    return nodes.reduce<(T & { ancestors?: T[] })[]>((flatNodes, node) => {
      if (node.nodes) {
        return [
          ...flatNodes,
          node,
          ...this.flatten(node.nodes as T[], ancestors ? [...ancestors, node] : [node])
        ];
      }

      return [...flatNodes, ancestors ? { ...node, ancestors } : node];
    }, []);
  },

  getAncestors<T extends TreeNode = TreeNode>(nodes: T[], id: T['id'], ancestors: T[] = []): T[] {
    const directParent = this.getParentNode(nodes, id);

    if (directParent) {
      ancestors.push(directParent);
      this.getAncestors(nodes, directParent.id, ancestors);
    }

    return ancestors;
  },

  appendTo<T extends TreeNode = TreeNode>(nodes: T[], id: T['id'], newNodes: T[]): T[] {
    if (id) {
      return this.mapNode(nodes, id, node => ({
        ...node,
        nodes: [...(node.nodes ?? []), ...newNodes]
      }));
    }

    return [...nodes, ...newNodes];
  },

  expandTo<T extends TreeNode = TreeNode>(nodes: T[], id: T['id'], collapseOthers = false): T[] {
    return nodes.map(node => {
      // leaf node
      if (node.id === id) {
        return {
          ...node,
          expanded: true
        };
      }

      // parent node
      if (node.nodes) {
        let expanded = collapseOthers ? false : node.expanded;

        if (this.getNode(node.nodes, id)) {
          expanded = true;
        }

        return {
          ...node,
          nodes: this.expandTo(node.nodes, id, collapseOthers),
          expanded
        };
      }

      return { ...node, expanded: collapseOthers ? false : node.expanded };
    });
  },

  getFirstChildNode<T extends TreeNode = TreeNode>(nodes: T[], id: T['id']): T | undefined {
    let firstChildNode;
    const node = this.getNode(nodes, id);
    if (node && node.expanded && node.nodes && node.nodes.length > 0) {
      const childNodes = node.nodes as T[];
      firstChildNode = childNodes[0];
    }
    return firstChildNode;
  },

  getAdjacentNode<T extends TreeNode = TreeNode>(nodes: T[], id: T['id']): T | undefined {
    let nextNode;
    const parent = this.getParentNode(nodes, id);
    if (parent && parent.nodes && parent.nodes.length > 0) {
      const childNodes = parent.nodes as T[];
      const currentIndex = childNodes.map(nodeEl => nodeEl.id).indexOf(id);
      if (currentIndex === childNodes.length - 1) {
        nextNode = this.getAdjacentNode(nodes, parent.id);
      } else {
        nextNode = childNodes[currentIndex + 1];
      }
    } else {
      const index = nodes.map(nodeEl => nodeEl.id).indexOf(id);
      nextNode = nodes[index + 1];
    }
    return nextNode;
  },

  getNextNode<T extends TreeNode = TreeNode>(nodes: T[], id: T['id']): T | undefined {
    let nextNode;
    const firstChildNode = this.getFirstChildNode(nodes, id);
    if (firstChildNode) {
      nextNode = firstChildNode;
    } else {
      nextNode = this.getAdjacentNode(nodes, id);
    }
    return nextNode;
  },

  getDeepestNode<T extends TreeNode = TreeNode>(nodes: T[], id: T['id']): T | undefined {
    const node = this.getNode(nodes, id);
    let lastNode = node;
    if (node && node.expanded && node.nodes && node.nodes.length > 0) {
      const childNodes = node.nodes as T[];
      const lastChildNode = childNodes[childNodes.length - 1];
      if (lastChildNode.expanded) {
        lastNode = this.getDeepestNode(nodes, lastChildNode.id);
      } else {
        lastNode = lastChildNode;
      }
    }
    return lastNode;
  },

  getPreviousNode<T extends TreeNode = TreeNode>(nodes: T[], id: T['id']): T | undefined {
    const parent = this.getParentNode(nodes, id);
    let previousNode;
    if (parent && parent.nodes && parent.nodes.length > 0) {
      const childNodes = parent.nodes;
      const currentIndex = childNodes.map(nodeEl => nodeEl.id).indexOf(id);
      if (currentIndex === 0) {
        previousNode = parent;
      } else {
        previousNode = this.getDeepestNode(nodes, childNodes[currentIndex - 1].id);
      }
    } else {
      const index = nodes.map(node => node.id).indexOf(id);
      if (nodes[index - 1]) previousNode = this.getDeepestNode(nodes, nodes[index - 1].id);
    }
    return previousNode;
  }
};

export default helpers;
